import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import {
  AttributeKindEnum,
  type HarvestOperationTemplateRevisionId,
  type TestTemplateId,
} from "@brewbase/db/types";
import {
  Copy,
  GripVerticalIcon,
  TableIcon,
  FileTextIcon,
  EyeIcon,
} from "lucide-react";
import { kindOptions } from "@/components/template-attribute-item/AttributeKindSelector";
import { CopyAttributesDialog } from "@/features/copy-attributes-dialog";
import { buildBlockModel, type BlockModel } from "./block-model";
import type {
  BaseTemplateAttribute,
  EditorConfig,
  EditorSection,
  Selection,
} from "./types";
import { useSelection } from "./use-selection";
import { ScalarBlock } from "./scalar-block";
import { TabularBlock } from "./tabular-block";
import { InsertButton } from "./insert-button";
import { EmptyState } from "./empty-state";
import { InstructionBlock } from "./instruction-block";
import { InspectorPanel } from "./inspector-panel";
import { EditorConfigProvider } from "./editor-config-context";

export function TemplateBlockEditor({ config }: { config: EditorConfig }) {
  const {
    templateId,
    isLocked,
    attributes,
    sections,
    addAttribute,
    duplicateAttribute,
    removeAttribute,
    reorderAttributes,
    updateAttribute,
    updateAttributeType,
    copyDialogType,
    renderPreview,
  } = config;

  const sectionModels = useMemo(
    () => sections.map((s) => buildBlockModel(s.select(attributes))),
    [sections, attributes],
  );

  const { selection, selectAttribute, selectColumn, clearSelection } =
    useSelection();
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [previewMode, setPreviewMode] = useState(false);

  const activeAttribute = useMemo(
    () => (activeId ? attributes.find((a) => a.id === activeId) ?? null : null),
    [activeId, attributes],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(Number(event.active.id));
  }, []);

  const makeDragEndHandler = useCallback(
    (flatIds: number[]) => (event: DragEndEvent) => {
      setActiveId(null);
      if (isLocked) return;
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const fromId = Number(active.id);
        const toId = Number(over.id);
        const oldIndex = flatIds.indexOf(fromId);
        const newIndex = flatIds.indexOf(toId);
        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = [...flatIds];
          const [moved] = newOrder.splice(oldIndex, 1);
          newOrder.splice(newIndex, 0, moved);
          reorderAttributes(newOrder);
        }
      }
    },
    [isLocked, reorderAttributes],
  );

  const makeInsertHandler = useCallback(
    (flatIds: number[], section: EditorSection) =>
      async (position: number, kind: AttributeKindEnum) => {
        const newId = await addAttribute(kind, section);
        if (!newId) return;
        const newOrder = [...flatIds];
        newOrder.splice(position, 0, newId);
        reorderAttributes(newOrder);
        selectAttribute(newId);
      },
    [addAttribute, reorderAttributes, selectAttribute],
  );

  const handleUpdateName = useCallback(
    (id: number, name: string) => {
      updateAttributeType(id, { name });
    },
    [updateAttributeType],
  );

  const handleUpdateContent = useCallback(
    (id: number, content: string) => {
      updateAttributeType(id, { content_html: content });
    },
    [updateAttributeType],
  );

  // Clicking empty space clears the selection, but only when the press also
  // started there. A drag that starts in the inspector (selecting a column's
  // name) and ends outside it fires its click on this shared container, which
  // used to close the inspector mid-edit.
  const backgroundPressRef = useRef(false);
  const handleBackgroundMouseDown = useCallback((e: React.MouseEvent) => {
    backgroundPressRef.current = e.target === e.currentTarget;
  }, []);
  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && backgroundPressRef.current) {
        clearSelection();
      }
      backgroundPressRef.current = false;
    },
    [clearSelection],
  );

  const selectedAttr = selection
    ? attributes.find((a) => a.id === selection.attributeId)
    : null;
  const inspectorOpen =
    selection !== null &&
    !(
      selection.type === "attribute" &&
      selectedAttr?.kind === AttributeKindEnum.INSTRUCTION
    );

  return (
    <EditorConfigProvider config={config}>
      <div className="h-full min-h-0 flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center gap-1 px-4 py-2 border-b shrink-0">
          {copyDialogType && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCopyDialogOpen(true)}
              disabled={isLocked}
              className="h-7 text-xs"
            >
              <Copy className="h-3.5 w-3.5 mr-1" />
              Copy from template
            </Button>
          )}

          {renderPreview && (
            <Button
              variant={previewMode ? "default" : "ghost"}
              size="sm"
              onClick={() => setPreviewMode((p) => !p)}
              className="ml-auto h-7 text-xs"
            >
              <EyeIcon className="h-3.5 w-3.5 mr-1" />
              Preview
            </Button>
          )}
        </div>

        {/* Content — each pane scrolls independently */}
        <div
          className="flex-1 min-h-0 flex justify-center"
          onMouseDown={handleBackgroundMouseDown}
          onClick={handleBackgroundClick}
        >
          {/* Blocks */}
          <div className="max-w-5xl w-full shrink min-w-0 overflow-y-auto pl-10 pr-4 py-6">
            <div className="space-y-6">
              {sections.map((section, si) => (
                <BlockSection
                  key={section.title ?? si}
                  title={section.title}
                  description={section.description}
                  model={sectionModels[si]}
                  sensors={sensors}
                  isLocked={isLocked}
                  selection={selection}
                  onDragStart={handleDragStart}
                  onDragEnd={makeDragEndHandler(sectionModels[si].flatIds)}
                  onInsert={makeInsertHandler(sectionModels[si].flatIds, section)}
                  onSelect={selectAttribute}
                  onSelectColumn={selectColumn}
                  onUpdateName={handleUpdateName}
                  onUpdateContent={handleUpdateContent}
                  onRemove={(id) => {
                    clearSelection();
                    removeAttribute(id);
                  }}
                  scalarInsertOnly={section.scalarInsertOnly}
                  compactEmpty={section.compactEmpty}
                  insertLabel={section.insertLabel}
                />
              ))}
            </div>
          </div>

          {/* Inspector — contextual, width transitions, own scroll */}
          <div
            className={
              "shrink-0 overflow-hidden transition-[width,margin] duration-200 ease-in-out " +
              (inspectorOpen ? "w-[360px] ml-4" : "w-0 ml-0")
            }
          >
            {selection && (
              <div className="w-[360px] h-full border border-border bg-background">
                <InspectorPanel
                  selection={selection}
                  attributes={attributes}
                  templateId={templateId}
                  isLocked={isLocked}
                  onUpdateAttributeType={updateAttributeType}
                  onUpdateSettings={updateAttribute}
                  onRemove={(id) => {
                    clearSelection();
                    removeAttribute(id);
                  }}
                  onDuplicate={
                    duplicateAttribute &&
                    ((id) =>
                      duplicateAttribute(id).then(selectAttribute, () => {
                        // The mutation already reported the failure.
                      }))
                  }
                  onClose={clearSelection}
                />
              </div>
            )}
          </div>
        </div>

        {/* Preview — dismissible full-width bottom panel, coexists with the
            inspector so edits and their live effect are visible together. */}
        {previewMode && renderPreview && (
          <div className="h-1/2 shrink-0 border-t border-border bg-background">
            {renderPreview({ onClose: () => setPreviewMode(false) })}
          </div>
        )}

        {copyDialogType && (
          <CopyAttributesDialog
            isOpen={isCopyDialogOpen}
            onClose={() => setIsCopyDialogOpen(false)}
            templateType={copyDialogType}
            targetTemplateId={
              templateId as HarvestOperationTemplateRevisionId | TestTemplateId
            }
          />
        )}

        {/* Shared drag overlay */}
        <DragOverlay dropAnimation={null}>
          {activeAttribute && <DragPreview attribute={activeAttribute} />}
        </DragOverlay>
      </div>
    </EditorConfigProvider>
  );
}

// ---------------------------------------------------------------------------
// Block Section — renders one DnD zone (e.g. parameters or recordings)
// ---------------------------------------------------------------------------

interface BlockSectionProps {
  title?: string;
  description?: string;
  model: BlockModel;
  sensors: ReturnType<typeof useSensors>;
  isLocked: boolean;
  selection: Selection;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onInsert: (position: number, kind: AttributeKindEnum) => void;
  onSelect: (id: number) => void;
  onSelectColumn: (id: number, sortOrder: number) => void;
  onUpdateName: (id: number, name: string) => void;
  onUpdateContent: (id: number, content: string) => void;
  onRemove: (id: number) => void;
  scalarInsertOnly?: boolean;
  insertLabel?: string;
  compactEmpty?: boolean;
}

function BlockSection({
  title,
  description,
  model,
  sensors,
  isLocked,
  selection,
  onDragStart,
  onDragEnd,
  onInsert,
  onSelect,
  onSelectColumn,
  onUpdateName,
  onUpdateContent,
  onRemove,
  scalarInsertOnly,
  insertLabel,
  compactEmpty,
}: BlockSectionProps) {
  const { blocks, flatIds, getInsertPosition } = model;

  return (
    <div>
      {(title || description) && (
        <div className="flex items-center gap-2 mb-2">
          {title && <h3 className="text-sm font-medium">{title}</h3>}
          {description && (
            <span className="text-xs text-muted-foreground">{description}</span>
          )}
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={flatIds.map(String)}
          strategy={verticalListSortingStrategy}
        >
          {blocks.length === 0 ? (
            <EmptyState
              onInsert={onInsert}
              isLocked={isLocked}
              parametersOnly={scalarInsertOnly}
              compact={compactEmpty}
              label={insertLabel}
            />
          ) : (
            <div className="space-y-0">
              {blocks.map((block, i) => {
                const blockKey =
                  block.type === "scalar"
                    ? `s-${block.attributes[0].id}`
                    : `${block.type[0]}-${block.attribute.id}`;
                return (
                  <Fragment key={blockKey}>
                    <InsertButton
                      position={getInsertPosition(i)}
                      onInsert={onInsert}
                      isLocked={isLocked}
                      scalarOnly={scalarInsertOnly}
                      scalarLabel={insertLabel}
                    />
                    {block.type === "scalar" ? (
                      <ScalarBlock
                        attributes={block.attributes}
                        selection={selection}
                        isLocked={isLocked}
                        onSelect={onSelect}
                        onUpdateName={onUpdateName}
                        onInsert={onInsert}
                        startPosition={getInsertPosition(i)}
                        hasBlockGap={i > 0}
                      />
                    ) : block.type === "tabular" ? (
                      <TabularBlock
                        attribute={block.attribute}
                        selection={selection}
                        isLocked={isLocked}
                        onSelectAttribute={onSelect}
                        onSelectColumn={onSelectColumn}
                        onUpdateName={(name) =>
                          onUpdateName(block.attribute.id, name)
                        }
                        onRemove={(id) => onRemove(id)}
                        hasBlockGap={i > 0}
                      />
                    ) : (
                      <InstructionBlock
                        attribute={block.attribute}
                        isLocked={isLocked}
                        hasBlockGap={i > 0}
                        onUpdateContent={onUpdateContent}
                        onRemove={(id) => onRemove(id)}
                      />
                    )}
                  </Fragment>
                );
              })}
              <InsertButton
                position={flatIds.length}
                onInsert={onInsert}
                isLocked={isLocked}
                scalarOnly={scalarInsertOnly}
                scalarLabel={insertLabel}
                alwaysVisible
              />
            </div>
          )}
        </SortableContext>
      </DndContext>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drag Preview
// ---------------------------------------------------------------------------

const kindIconMap = new Map(
  kindOptions.map((k) => [k.value, { icon: k.icon, label: k.label }]),
);

function DragPreview({ attribute }: { attribute: BaseTemplateAttribute }) {
  const kindInfo = kindIconMap.get(attribute.kind);
  const isTabular = attribute.kind === AttributeKindEnum.TABULAR_DATA;
  const isInstruction = attribute.kind === AttributeKindEnum.INSTRUCTION;

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-background border border-border shadow-md w-[320px]">
      <GripVerticalIcon className="h-3 w-3 text-muted-foreground shrink-0" />
      {isTabular && (
        <TableIcon className="h-4 w-4 text-muted-foreground shrink-0" />
      )}
      {isInstruction && (
        <FileTextIcon className="h-4 w-4 text-muted-foreground shrink-0" />
      )}
      <span className="text-base font-medium truncate flex-1">
        {attribute.name || (
          <span className="text-muted-foreground italic">Untitled</span>
        )}
      </span>
      {kindInfo && (
        <span className="flex items-center gap-1 text-sm text-muted-foreground shrink-0">
          <span className="[&_svg]:h-3 [&_svg]:w-3">{kindInfo.icon}</span>
          {kindInfo.label}
        </span>
      )}
    </div>
  );
}
